import { PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer } from "recharts";

const LIKERT_COLORS = ["#8ed973", "#2a738f", "#a02b93"];

export default function MiniPollChart({ post }) {
  // Likert polls: horizontal bars per statement
  if (post.likert_aggregates && post.likert_aggregates.length > 0) {
    const rows = post.likert_aggregates.slice(0, 5);
    return (
      <div className="mt-2 space-y-1.5" data-testid="mini-poll-likert">
        {rows.map((row, i) => (
          <div key={i} className="h-1.5 bg-neutral-100 border border-neutral-200">
            <div
              className="h-full"
              style={{ width: `${row.low_pct || 0}%`, background: LIKERT_COLORS[i % LIKERT_COLORS.length] }}
            />
          </div>
        ))}
      </div>
    );
  }

  const options = post.poll_options || [];
  if (options.length === 0) return null;
  const colors = post.chart_colors || [];
  const counts = post.poll_counts || {};
  const total = post.poll_total_votes || 0;
  const data = options.map((opt, i) => ({
    name: opt,
    votes: counts[opt] || 0,
    fill: colors[i] || "#d97706",
  }));
  const type = post.chart_type || "bar";

  if (total === 0) {
    // Still show all options so readers can see what's being voted on even before
    // the first vote lands. Each option gets a labelled empty bar.
    return (
      <div className="mt-2 space-y-1.5" data-testid="mini-poll-empty">
        <div className="mono text-[9px] uppercase tracking-[0.2em] text-neutral-400">
          Awaiting first vote
        </div>
        {options.map((opt, i) => (
          <div key={i} className="text-[10px]">
            <div className="flex justify-between items-baseline gap-2 leading-tight">
              <span className="truncate text-neutral-700 flex items-center gap-1.5 min-w-0">
                <span
                  className="inline-block w-2 h-2 shrink-0 border border-neutral-300"
                  style={{ background: colors[i] || "#d97706" }}
                  aria-hidden
                />
                <span className="truncate">{opt}</span>
              </span>
              <span className="mono tabular-nums text-neutral-400 shrink-0">0</span>
            </div>
            <div className="h-1.5 bg-neutral-100 border border-neutral-200 mt-0.5" />
          </div>
        ))}
      </div>
    );
  }

  // Bar-type polls get an inline labelled horizontal bar for every option — even
  // options with zero votes still appear as a labelled empty bar so readers can
  // see the full range of choices at a glance.
  if (type === "bar") {
    return (
      <div className="mt-2 space-y-1.5" data-testid="mini-poll-bar">
        {options.map((opt, i) => {
          const c = counts[opt] || 0;
          const pct = total > 0 ? (c / total) * 100 : 0;
          const color = colors[i] || "#d97706";
          return (
            <div key={i} className="text-[10px]">
              <div className="flex justify-between items-baseline gap-2 leading-tight">
                <span className="truncate text-neutral-700 flex items-center gap-1.5 min-w-0">
                  <span
                    className="inline-block w-2 h-2 shrink-0 border border-neutral-300"
                    style={{ background: color }}
                    aria-hidden
                  />
                  <span className="truncate">{opt}</span>
                </span>
                <span className="mono tabular-nums text-neutral-500 shrink-0">
                  {c === 0 ? "0" : `${Math.round(pct)}%`}
                </span>
              </div>
              <div className="h-1.5 bg-neutral-100 border border-neutral-200 mt-0.5">
                <div
                  className="h-full"
                  style={{ width: `${Math.max(pct, 0)}%`, background: color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="mt-2" data-testid={`mini-poll-chart-${type}`}>
      <div className="h-16 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {type === "pie" ? (
            <PieChart>
              <Pie
                data={data}
                dataKey="votes"
                nameKey="name"
                innerRadius={12}
                outerRadius={28}
                paddingAngle={1}
                isAnimationActive={false}
              >
                {data.map((d, i) => (
                  <Cell key={i} fill={d.fill} stroke="none" />
                ))}
              </Pie>
            </PieChart>
          ) : (
            <LineChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
              <Line
                type="monotone"
                dataKey="votes"
                stroke={colors[0] || "#0A0A0A"}
                strokeWidth={2}
                dot={{ r: 2 }}
                isAnimationActive={false}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
      {/* Compact legend so pie / line thumbnails also tell the reader what each
          slice / point represents, and surface any option that has 0 votes. */}
      <ul className="mt-1.5 space-y-0.5">
        {options.map((opt, i) => {
          const c = counts[opt] || 0;
          const pct = total > 0 ? Math.round((c / total) * 100) : 0;
          const color = colors[i] || "#d97706";
          return (
            <li key={i} className="flex items-center justify-between gap-2 text-[10px] leading-tight">
              <span className="truncate text-neutral-700 flex items-center gap-1.5 min-w-0">
                <span
                  className="inline-block w-2 h-2 shrink-0 border border-neutral-300"
                  style={{ background: color }}
                  aria-hidden
                />
                <span className="truncate">{opt}</span>
              </span>
              <span className="mono tabular-nums text-neutral-500 shrink-0">
                {c === 0 ? "0" : `${pct}%`}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
